import { MapUtils } from '../../utility/map-utils.js';
import { Country } from '../serializable/country.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { Address } from './address.js';
import { GeoCoordinates } from './geo-coordinates.js';
import { PhotoReference } from './file-reference/photo-reference.js';

export class MapLocation extends DataModel {
  static readonly TYPE_ID: string = '32aec9ffdf7a4b378a50b0a2f241c47f';

  constructor(
    public readonly address: Address | null,
    public readonly coordinates: GeoCoordinates | null,
    public readonly mapPreview: PhotoReference | null,
  ) {
    super();
  }

  get $typeId() {
    return MapLocation.TYPE_ID;
  }

  get isMapPreviewAvailable() {
    return this.mapPreview !== null;
  }

  static empty(): MapLocation {
    return new MapLocation(null, null, null);
  }

  static fromCountry(country: Country): MapLocation {
    return new MapLocation(
        Address.create({ country: country.toString() }),
        null,
        null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): MapLocation {
    return new MapLocation(
        context.fromDataOrNull<Address>(MapUtils.getRaw(map, 'address') as SerializedData),
        context.fromDataOrNull<GeoCoordinates>(MapUtils.getRaw(map, 'coordinates') as SerializedData),
        context.fromDataOrNull<PhotoReference>(MapUtils.getRaw(map, 'mapPreview') as SerializedData),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'address': context.toDataOrNull(this.address),
      'coordinates': context.toDataOrNull(this.coordinates),
      'mapPreview': context.toDataOrNull(this.mapPreview),
    };
  }

  clone(): this {
    return new MapLocation(
        this.address?.clone() ?? null,
        this.coordinates?.clone() ?? null,
        this.mapPreview?.clone() ?? null,
    ) as this;
  }

  isSame(other: MapLocation): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.address, this.address) &&
      isSameData(other.coordinates, this.coordinates) &&
        isSameData(other.mapPreview, this.mapPreview);
  }

  copyWith(data: Partial<MapLocation>): this {
    return new MapLocation(
        data.address ?? (this.address?.clone() ?? null),
        data.coordinates ?? (this.coordinates?.clone() ?? null),
        data.mapPreview ?? (this.mapPreview?.clone() ?? null),
    ) as this;
  }
}

export class MapLocationBuilder extends DataModelBuilder<MapLocation> {
  build(context: SerializationContext, map: SerializedData): MapLocation {
    return MapLocation.fromMap(context, map);
  }
}
